/**
 * Voice Command Parser for Xpenso
 * Parses voice commands like:
 *   EN: "200 food" / "salary 5000" / "expense 200 food"
 *   BN: "२०० खाबार" / "बेतन ५०००"
 *
 * Strategy:
 *   1. Extract amount (number)
 *   2. Match remaining words against category names (with fuzzy matching)
 *   3. Derive transaction type from category. Type (Expense/Income)
 *   4. Explicit type keyword ("expense"/"income") is optional override
 *   5. Match accounts using fuzzy matching (handles typos/custom names)
 *   6. Return confidence scores for both category and account
 */
import {
    bengaliToEnglishNumber,
    fuzzyMatchVoiceText,
    fuzzyMatchVoiceInput,
    wordNumberToDigit,
} from './helper';
import { getAllAliases, getAliasesByLanguage } from './categoryAliases';

// Explicit type keywords (optional — category.type is the primary source)
const TYPE_KEYWORDS = {
    expense: {
        en: ['expense', 'spent', 'spend', 'cost', 'pay', 'paid', 'bought', 'purchase', 'minus'],
        bn: ['খরচ', 'ব্যয়', 'কিনলাম', 'কিনেছি', 'দিলাম', 'দিয়েছি', 'পেমেন্ট', 'মাইনাস'],
    },
    income: {
        en: ['income', 'earn', 'earned', 'received', 'got', 'plus', 'deposit'],
        bn: ['আয়', 'ইনকাম', 'পেলাম', 'পেয়েছি', 'জমা', 'প্লাস'],
    },
    transfer: {
        en: ['transfer', 'send', 'move'],
        bn: ['ট্রান্সফার', 'ট্রান্সফর', 'পাঠা', 'পাঠান', 'নড়ান'],
    },
    withdraw: {
        en: ['withdraw', 'withdrawal', 'cash out'],
        bn: ['উইথড্র', 'উইথড্রয়াল', 'বের করা', 'বের করুন'],
    },
};

// Get category aliases from categoryAliases.js
const CATEGORY_ALIASES = getAllAliases();

// Noise words to strip before category matching
const NOISE_WORDS = new Set([
    'taka',
    'tk',
    'bdt',
    'dollar',
    'dollars',
    'rupees',
    'rupee',
    'rs',
    'for',
    'on',
    'in',
    'to',
    'the',
    'a',
    'an',
    'of',
    'and',
    'with',
    'from',
    'category',
    'account',
    'wallet',
    'টাকা',
    'টাকার',
    'ক্যাটাগরি',
    'হিসাব',
    'থেকে',
    'জন্য',
    'এ',
    'তে',
    'এর',
    'এবং',
    'অ্যাকাউন্ট',
    'একাউন্ট',
    'ওয়ালেট',
    // Bengali number words
    'শূন্য',
    'শূণ্য',
    'এক',
    'দুই',
    'তিন',
    'চার',
    'পাঁচ',
    'ছয়',
    'সাত',
    'আট',
    'নয়',
    'দশ',
    'এগারো',
    'বারো',
    'তেরো',
    'চৌদ্দ',
    'পনের',
    'ষোল',
    'সতের',
    'আঠার',
    'উনিশ',
    'বিশ',
    'ত্রিশ',
    'চল্লিশ',
    'পঞ্চাশ',
    'ষাট',
    'সত্তর',
    'আশি',
    'নব্বই',
    'শত',
    'হাজার',
    'লক্ষ',
    'লাখ',
    'কোটি',
    // Also strip type keywords so they don't pollute category matching
    ...TYPE_KEYWORDS.expense.en,
    ...TYPE_KEYWORDS.expense.bn,
    ...TYPE_KEYWORDS.income.en,
    ...TYPE_KEYWORDS.income.bn,
    ...TYPE_KEYWORDS.transfer.en,
    ...TYPE_KEYWORDS.transfer.bn,
    ...TYPE_KEYWORDS.withdraw.en,
    ...TYPE_KEYWORDS.withdraw.bn,
]);

const ACCOUNT_KEYWORDS = new Set([
    'from',
    'account',
    'wallet',
    'to',
    'থেকে',
    'অ্যাকাউন্ট',
    'একাউন্ট',
    'ওয়ালেট',
    'হিসাব',
]);

/**
 * Parse a voice command string into structured transaction data
 * @param {string} text - Raw speech-to-text transcript
 * @param {string} language - 'en' or 'bn'
 * @param {Array} categories - Available categories [{id, name, type}]
 * @param {Array} accounts - Available accounts [{id, name}]
 * @returns {Object} { transactionType, amount, categoryName, categoryId, accountId, accountName, missing: [] }
 */
export const parseVoiceCommand = (text, language = 'en', categories = [], accounts = []) => {
    if (!text || text.trim() === '') {
        return {
            transactionType: null,
            amount: null,
            categoryName: null,
            categoryId: null,
            accountId: null,
            accountName: null,
            missing: ['amount', 'category'],
        };
    }

    const normalizedText = text.toLowerCase().trim();
    const words = normalizedText.split(/\s+/);

    // 1. Check for explicit type keyword (optional override)
    let explicitType = null;
    for (const word of words) {
        if (TYPE_KEYWORDS.expense.en.includes(word) || TYPE_KEYWORDS.expense.bn.includes(word)) {
            explicitType = 'expense';
            break;
        }
        if (TYPE_KEYWORDS.income.en.includes(word) || TYPE_KEYWORDS.income.bn.includes(word)) {
            explicitType = 'income';
            break;
        }
        if (TYPE_KEYWORDS.transfer.en.includes(word) || TYPE_KEYWORDS.transfer.bn.includes(word)) {
            explicitType = 'transfer';
            break;
        }
        if (TYPE_KEYWORDS.withdraw.en.includes(word) || TYPE_KEYWORDS.withdraw.bn.includes(word)) {
            explicitType = 'withdraw';
            break;
        }
    }

    // 2. Extract amount
    let amount = null;
    // Step A: Convert Bengali digits (০-৯) to English digits (0-9) first
    let amountText = bengaliToEnglishNumber(normalizedText);
    // Step B: Convert word numbers ("seven", "lakh", "এক", "হাজার") to digits
    amountText = wordNumberToDigit(amountText);
    // Step C: Convert any remaining Bengali digits that wordNumberToDigit may have produced
    amountText = bengaliToEnglishNumber(amountText);
    // Step D: Sum adjacent numbers that form a composite amount
    // e.g., "100000 18000 taka salary" → sum 100000 + 18000 = 118000
    const allNumbers = [];
    const amountTokens = amountText.split(/\s+/);
    for (const token of amountTokens) {
        const cleaned = token.replace(/,/g, '');
        if (/^\d+\.?\d*$/.test(cleaned)) {
            allNumbers.push(parseFloat(cleaned));
        } else {
            // Stop collecting numbers when we hit a non-number word
            if (allNumbers.length > 0) break;
        }
    }
    if (allNumbers.length > 0) {
        const total = allNumbers.reduce((sum, n) => sum + n, 0);
        if (total > 0) amount = total;
    }

    // 3. Filter words for category/account matching
    const remainingWords = words.filter((word) => {
        const cleanWord = word.replace(/[,।.]/g, '');
        if (NOISE_WORDS.has(cleanWord)) return false;
        const converted = bengaliToEnglishNumber(cleanWord);
        return !/^\d+\.?\d*$/.test(converted);
    });

    // 3b. Pre-identify words that match account names — exclude them from category matching
    //    This prevents "cash" (default account) from falsely matching "Refund & Cashback" category
    const accountMatchedWords = new Set();
    if (Array.isArray(accounts) && accounts.length > 0) {
        for (const word of remainingWords) {
            if (word.length < 2) continue;
            for (const acct of accounts) {
                const acctName = (acct.name || '').toLowerCase();
                // Exact match or very close match (account name equals the word)
                if (acctName === word || acctName.startsWith(word) || word.startsWith(acctName)) {
                    accountMatchedWords.add(word);
                    break;
                }
            }
        }
    }

    // 4. Extract category using fuzzy matching
    const categoryRemainingWords = remainingWords.filter((w) => !accountMatchedWords.has(w));

    let categoryName = null;
    let categoryId = null;
    let categoryType = null;
    let categoryConfidence = 0;
    let categoryMatches = [];

    // IMPORTANT: Skip category matching if transfer/withdraw is explicitly detected
    // Transfer/Withdraw type has PRIORITY over category matching
    if (categories.length > 0 && explicitType !== 'transfer' && explicitType !== 'withdraw') {
        let matched = null;

        // When explicit type keyword is given, prefer categories of that type
        const preferredCategories = explicitType
            ? categories.filter((c) => c.type.toLowerCase() === explicitType)
            : categories;

        // Get language-specific aliases (primary) and all aliases (fallback)
        const languageSpecificAliases = getAliasesByLanguage(language);
        const allAliases = getAllAliases();

        // Strategy 1: Try alias mapping first — prioritize language-specific aliases
        //   Use categoryRemainingWords (account-matched words excluded)
        for (const word of categoryRemainingWords) {
            let alias = languageSpecificAliases[word];
            if (!alias) {
                alias = allAliases[word];
            }
            if (alias) {
                matched =
                    preferredCategories.find((c) => c.name.toLowerCase().includes(alias)) ||
                    categories.find((c) => c.name.toLowerCase().includes(alias));
                if (matched) {
                    categoryName = matched.name;
                    categoryId = matched.id;
                    categoryType = matched.type;
                    categoryConfidence = 95;
                    break;
                }
            }
        }

        // Strategy 2: Try fuzzy matching on individual words
        if (!matched && categoryRemainingWords.length > 0) {
            for (const word of categoryRemainingWords) {
                if (word.length < 2) continue;

                const matches = fuzzyMatchVoiceInput(word, preferredCategories, 70);
                if (matches.length > 0) {
                    const best = matches[0];
                    categoryName = best.name;
                    categoryId = best.id;
                    categoryType = best.type;
                    categoryConfidence = best.confidence;
                    categoryMatches = matches;
                    matched = best;
                    break;
                }
            }
        }

        // Strategy 4: Handle words that are CATEGORY_ALIASES but in NOISE_WORDS
        //    (e.g., "salary", "বেতন", "সেলারি" — stripped as noise but valid categories)
        if (!matched) {
            for (const word of words) {
                const cleanWord = word.replace(/[,।.]/g, '');
                const alias = CATEGORY_ALIASES[cleanWord];
                if (alias) {
                    const preferList = explicitType
                        ? categories.filter((c) => c.type.toLowerCase() === explicitType)
                        : categories;
                    matched =
                        preferList.find((c) => c.name.toLowerCase().includes(alias)) ||
                        categories.find((c) => c.name.toLowerCase().includes(alias));
                    if (matched) {
                        categoryName = matched.name;
                        categoryId = matched.id;
                        categoryType = matched.type;
                        categoryConfidence = 90;
                        break;
                    }
                }
            }
        }

        // Strategy 5: Try fuzzy match on fallback categories if explicit type set
        if (!matched && explicitType) {
            const fuzzyMatch = fuzzyMatchVoiceText(remainingWords.join(' '), categories, 70);
            if (fuzzyMatch) {
                categoryName = fuzzyMatch.name;
                categoryId = fuzzyMatch.id;
                categoryType = fuzzyMatch.type;
                categoryConfidence = fuzzyMatch.confidence * 0.9;
            }
        }
    } // ← end of if (categories.length > 0)

    // 4. Extract account(s) from speech using fuzzy matching
    // For transfer/withdraw: extract TWO accounts (from and to)
    // For expense/income: extract ONE account

    let accountId = null;
    let accountName = null;
    let accountConfidence = 0;
    let accountMatches = [];
    let toAccountId = null;
    let toAccountName = null;
    let toAccountConfidence = 0;
    let toAccountMatches = [];

    if (Array.isArray(accounts) && accounts.length > 0) {
        if (explicitType === 'transfer' || explicitType === 'withdraw') {
            // Extract TWO accounts for transfer
            // Look for separator words like "থেকে" (from) and "এ" (to) or "থেকে...এ" pattern
            const fromKeywords = ['থেকে', 'from'];
            const toKeywords = ['এ', 'তে', 'to'];

            let fromAccountWords = [];
            let toAccountWords = [];
            let foundFromIdx = -1;
            let foundToIdx = -1;

            // Find positions of from/to keywords
            for (let i = 0; i < words.length; i++) {
                if (fromKeywords.includes(words[i]) && foundFromIdx === -1) {
                    foundFromIdx = i;
                }
                if (toKeywords.includes(words[i]) && foundToIdx === -1 && i > foundFromIdx) {
                    foundToIdx = i;
                }
            }

            // Extract words between boundaries
            // For English pattern: "transfer 20 from bkash to citybank"
            // For Bengali pattern: "bkash থেকে citybank এ 20 transfer"
            if (foundFromIdx !== -1 && foundToIdx !== -1 && foundFromIdx < foundToIdx) {
                // Bengali natural order: "<source> থেকে <target> এ ..." — an account-like word
                // sits right BEFORE থেকে (not a number / type keyword / noise word).
                const isAccountWord = (w) => {
                    const clean = w.replace(/[,।.]/g, '');
                    return (
                        clean.length > 0 &&
                        !NOISE_WORDS.has(clean) &&
                        !/^\d+\.?\d*$/.test(bengaliToEnglishNumber(clean))
                    );
                };
                const beforeFrom = [];
                if (words[foundFromIdx] === 'থেকে') {
                    for (let j = foundFromIdx - 1; j >= 0 && beforeFrom.length < 2; j--) {
                        if (!isAccountWord(words[j])) break;
                        beforeFrom.unshift(words[j]);
                    }
                }

                if (beforeFrom.length > 0) {
                    fromAccountWords = beforeFrom;
                    toAccountWords = words.slice(foundFromIdx + 1, foundToIdx);
                } else {
                    // Pattern 1: "... থেকে accountName এ ..."
                    // Extract word(s) immediately after থেকে and before এ
                    fromAccountWords = words.slice(foundFromIdx + 1, foundToIdx);
                    // Extract word(s) immediately after এ
                    if (foundToIdx + 1 < words.length) {
                        toAccountWords = words.slice(foundToIdx + 1, foundToIdx + 3); // take 1-2 words
                    }
                }
            } else if (foundFromIdx !== -1) {
                // Pattern 2: "... থেকে accountName ... (no 'to' keyword)"
                // Extract word(s) immediately after থেকে
                if (foundFromIdx + 1 < words.length) {
                    fromAccountWords = words.slice(foundFromIdx + 1, foundFromIdx + 3); // take 1-2 words
                }
                // Try to find to account elsewhere
                if (foundToIdx !== -1) {
                    toAccountWords = words.slice(foundToIdx + 1, foundToIdx + 3);
                }
            } else if (foundToIdx !== -1) {
                // Pattern 3: "... to accountName ..." (no 'from' keyword)
                // Extract word(s) immediately after 'to'
                if (foundToIdx + 1 < words.length) {
                    toAccountWords = words.slice(foundToIdx + 1, foundToIdx + 3);
                }
            }

            // Match FROM account
            if (fromAccountWords.length > 0) {
                const fromText = fromAccountWords.join(' ');
                const matches = fuzzyMatchVoiceInput(fromText, accounts, 70);
                if (matches.length > 0) {
                    const best = matches[0];
                    accountId = best.id;
                    accountName = best.name;
                    accountConfidence = best.confidence;
                    accountMatches = matches;
                }
            }

            // Match TO account
            if (toAccountWords.length > 0) {
                const toText = toAccountWords.join(' ');
                const matches = fuzzyMatchVoiceInput(toText, accounts, 70);
                if (matches.length > 0) {
                    const best = matches[0];
                    toAccountId = best.id;
                    toAccountName = best.name;
                    toAccountConfidence = best.confidence;
                    toAccountMatches = matches;
                }
            }
        } else {
            // Single account for expense/income

            // Strategy A: Look for account keyword and fuzzy match nearby words
            for (let i = 0; i < words.length; i++) {
                if (ACCOUNT_KEYWORDS.has(words[i])) {
                    // Try 2-word combo after keyword first
                    if (i + 2 < words.length) {
                        const twoWordAfter = `${words[i + 1]} ${words[i + 2]}`;
                        const matches = fuzzyMatchVoiceInput(twoWordAfter, accounts, 75);
                        if (matches.length > 0) {
                            const best = matches[0];
                            accountId = best.id;
                            accountName = best.name;
                            accountConfidence = best.confidence;
                            accountMatches = matches;
                            break;
                        }
                    }
                    // Try single word after keyword
                    if (i + 1 < words.length) {
                        const wordAfter = words[i + 1];
                        const matches = fuzzyMatchVoiceInput(wordAfter, accounts, 75);
                        if (matches.length > 0) {
                            const best = matches[0];
                            accountId = best.id;
                            accountName = best.name;
                            accountConfidence = best.confidence;
                            accountMatches = matches;
                            break;
                        }
                    }
                    // A strong single-word hit right before the keyword ("... bkash account")
                    // wins over a 2-word combo, which can tie on the loose sound-alike score
                    // (e.g. "food bkash" vs "Cash" both end in "ash").
                    if (i - 1 >= 0) {
                        const strong = fuzzyMatchVoiceInput(words[i - 1], accounts, 90);
                        if (strong.length > 0) {
                            const best = strong[0];
                            accountId = best.id;
                            accountName = best.name;
                            accountConfidence = best.confidence;
                            accountMatches = strong;
                            break;
                        }
                    }
                    // Try 2-word combo before keyword
                    if (i - 2 >= 0) {
                        const twoWordBefore = `${words[i - 2]} ${words[i - 1]}`;
                        const matches = fuzzyMatchVoiceInput(twoWordBefore, accounts, 75);
                        if (matches.length > 0) {
                            const best = matches[0];
                            accountId = best.id;
                            accountName = best.name;
                            accountConfidence = best.confidence;
                            accountMatches = matches;
                            break;
                        }
                    }
                    // Try single word before keyword
                    if (i - 1 >= 0) {
                        const wordBefore = words[i - 1];
                        const matches = fuzzyMatchVoiceInput(wordBefore, accounts, 75);
                        if (matches.length > 0) {
                            const best = matches[0];
                            accountId = best.id;
                            accountName = best.name;
                            accountConfidence = best.confidence;
                            accountMatches = matches;
                            break;
                        }
                    }
                }
            }

            // Strategy B: If no keyword found, try fuzzy matching full text
            if (!accountId && words.length > 0) {
                const textMatch = fuzzyMatchVoiceText(words.join(' '), accounts, 75);
                if (textMatch) {
                    accountId = textMatch.id;
                    accountName = textMatch.name;
                    accountConfidence = textMatch.confidence;
                }
            }

            // Strategy C: Try fuzzy match on each word individually
            if (!accountId) {
                const skipWords = new Set([
                    ...NOISE_WORDS,
                    ...(categoryName ? categoryName.toLowerCase().split(/\s+/) : []),
                ]);

                for (const word of words) {
                    if (word.length < 2) continue;
                    const cleanWord = word.replace(/[,।.]/g, '');
                    if (skipWords.has(cleanWord)) continue;
                    const converted = bengaliToEnglishNumber(cleanWord);
                    if (/^\d+\.?\d*$/.test(converted)) continue;
                    if (CATEGORY_ALIASES[cleanWord]) continue;

                    const matches = fuzzyMatchVoiceInput(cleanWord, accounts, 70);
                    if (matches.length > 0) {
                        const best = matches[0];
                        accountId = best.id;
                        accountName = best.name;
                        accountConfidence = best.confidence;
                        accountMatches = matches;
                        break;
                    }
                }
            }
        } // ← end of else (single account)
    } // ← end of if (Array.isArray(accounts))

    // 5. Determine transaction type: explicit type keyword has PRIORITY over category.type
    // Transfer/Withdraw keywords have the highest priority
    let transactionType = null;
    if (explicitType === 'transfer' || explicitType === 'withdraw') {
        // Transfer/Withdraw type keyword detected - use it directly
        transactionType = explicitType;
    } else if (categoryType) {
        // For expense/income, use category type
        transactionType = categoryType.toLowerCase(); // 'Expense' → 'expense'
    } else if (explicitType) {
        // Other explicit types (expense/income keywords)
        transactionType = explicitType;
    } else if (amount) {
        // Default to expense if only amount given
        transactionType = 'expense';
    }

    // Build missing fields
    const missing = [];
    if (!amount) missing.push('amount');
    if (!categoryId) missing.push('category');

    return {
        transactionType,
        amount: transactionType === 'expense' && amount ? -Math.abs(amount) : amount,
        categoryName,
        categoryId,
        categoryConfidence,
        categoryMatches,
        accountId,
        accountName,
        accountConfidence,
        accountMatches,
        toAccountId,
        toAccountName,
        toAccountConfidence,
        toAccountMatches,
        rawText: text,
        missing,
    };
};
